import React, { PropsWithChildren, ReactElement } from 'react';
import { SEOTableColumn } from 'src/api/models/SEOTable';
import { ExternalUrlAnchor, GoToSectionAnchor, InternalUrlAnchor } from './AnchorToController.styles';
import { HEADER_HEIGHT_IN_PIXELS } from 'src/molecules/Shared/2023_Q4/Header/Header.styles';
import { headingToKey } from 'src/functions/getTableOfContentDictionary';

type AnchorToControllerProps = Pick<SEOTableColumn, 'anchorTo'> & PropsWithChildren;

const anchorIsExternalUrl = (anchorTo: string): boolean =>
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/.test(anchorTo);

const anchorIsInternalUrl = (anchorTo: string): boolean => /^\/(?!\/)/.test(anchorTo);

const AnchorToController = ({ anchorTo, children }: AnchorToControllerProps): JSX.Element => {
  if (!anchorTo) return children as ReactElement;

  if (anchorIsExternalUrl(anchorTo)) {
    return (
      <ExternalUrlAnchor
        href={anchorTo}
        target='_blank'
        rel='noopener noreferrer'
      >
        {children}
      </ExternalUrlAnchor>
    );
  }

  if (anchorIsInternalUrl(anchorTo)) {
    return <InternalUrlAnchor to={anchorTo}>{children}</InternalUrlAnchor>;
  }

  const anchorToAsKey = headingToKey(anchorTo);

  const handleGoToSection = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const element = document.querySelector(`#${anchorToAsKey}`);

    if (!element || typeof window === 'undefined') return;

    window.scrollTo({
      top: element.getBoundingClientRect().top + window.scrollY - HEADER_HEIGHT_IN_PIXELS,
      behavior: 'smooth',
    });
  };

  return (
    <GoToSectionAnchor
      href={`#${anchorToAsKey}`}
      onClick={handleGoToSection}
    >
      {children}
    </GoToSectionAnchor>
  );
};

export default AnchorToController;
